import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaBars,
  FaSave,
  FaUndo,
  FaPlus,
  FaTrash,
  FaArrowUp,
  FaArrowDown,
  FaUpload,
} from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

const MENU_ICONS = ['FaBuilding', 'FaCalculator', 'FaChartLine', 'FaGlobe', 'FaShieldAlt', 'FaUserTie'];

const MAIN_LINK_KEYS = [
  { key: 'pricing', label: 'Pricing', hasPath: true },
  { key: 'blog', label: 'Blog', hasPath: true },
  { key: 'whyBooksComply', label: 'Why BooksComply', hasPath: true },
  { key: 'login', label: 'Login', hasPath: false },
  { key: 'getInTouch', label: 'Get in Touch', hasPath: false },
];

const CONSULTATION_KEYS = [
  { key: 'default', label: 'Default' },
  { key: 'usaFormation', label: 'USA Company Formation' },
  { key: 'taxReturn', label: 'Tax Return' },
  { key: 'bookkeeping', label: 'Bookkeeping' },
  { key: 'cfo', label: 'CFO Advisory' },
  { key: 'zoho', label: 'Zoho Advisory' },
];

const emptyMenuItem = () => ({ label: '', path: '', iconName: 'FaBuilding' });
const emptyFooterLink = () => ({ label: '', path: '' });
const emptyFooterSection = () => ({ title: '', links: [emptyFooterLink()] });

const defaultForm = () => ({
  logo: {
    menuLogoLight: '',
    menuLogoDark: '',
    footerLogo: '',
    logoAlt: 'BooksComply'
  },
  menu: {
    servicesItems: [],
    industryItems: [],
    mainLinks: {
      pricing: { label: 'Pricing', path: '/pricing', show: true },
      blog: { label: 'Blog', path: '/blog', show: true },
      whyBooksComply: { label: 'Why BooksComply', path: '/why-bookscomply', show: true },
      login: { label: 'Login', show: true },
      getInTouch: { label: 'Get in Touch', show: true },
    },
  },
  footer: {
    tagline: '',
    contactEmail: '',
    copyrightText: '',
    freeConsultationTexts: {
      default: '',
      usaFormation: '',
      taxReturn: '',
      bookkeeping: '',
      cfo: '',
      zoho: '',
    },
    socialLinks: { linkedin: '', twitter: '', instagram: '' },
    sections: [],
  },
});

const NavigationCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('menu');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/settings/admin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        setForm({
          logo: {
            menuLogoLight: d.logo?.menuLogoLight || '',
            menuLogoDark: d.logo?.menuLogoDark || '',
            footerLogo: d.logo?.footerLogo || '',
            logoAlt: d.logo?.logoAlt || 'BooksComply'
          },
          menu: {
            servicesItems: (d.menu?.servicesItems || []).map((item) => ({
              _id: item._id,
              label: item.label || '',
              path: item.path || '',
              iconName: item.iconName || 'FaBuilding',
            })),
            industryItems: (d.menu?.industryItems || []).map((item) => ({
              _id: item._id,
              label: item.label || '',
              path: item.path || '',
              iconName: item.iconName || 'FaBuilding',
            })),
            mainLinks: {
              pricing: { label: 'Pricing', path: '/pricing', show: true, ...d.menu?.mainLinks?.pricing },
              blog: { label: 'Blog', path: '/blog', show: true, ...d.menu?.mainLinks?.blog },
              whyBooksComply: { label: 'Why BooksComply', path: '/why-bookscomply', show: true, ...d.menu?.mainLinks?.whyBooksComply },
              login: { label: 'Login', show: true, ...d.menu?.mainLinks?.login },
              getInTouch: { label: 'Get in Touch', show: true, ...d.menu?.mainLinks?.getInTouch },
            },
          },
          footer: {
            tagline: d.footer?.tagline || '',
            contactEmail: d.footer?.contactEmail || '',
            copyrightText: d.footer?.copyrightText || '',
            freeConsultationTexts: {
              default: '',
              usaFormation: '',
              taxReturn: '',
              bookkeeping: '',
              cfo: '',
              zoho: '',
              ...d.footer?.freeConsultationTexts,
            },
            socialLinks: {
              linkedin: '',
              twitter: '',
              instagram: '',
              ...d.footer?.socialLinks,
            },
            sections: (d.footer?.sections || []).map((sec) => ({
              _id: sec._id,
              title: sec.title || '',
              links: (sec.links || []).map((link) => ({
                _id: link._id,
                label: link.label || '',
                path: link.path || '',
              })),
            })),
          },
        });
      }
    } catch (err) {
      toastError('Failed to load navigation settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/settings/admin`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Navigation settings saved');
        fetchSettings();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore default menu and footer layout?')) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/settings/admin/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Defaults restored');
        fetchSettings();
      }
    } catch (err) {
      toastError('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/home/admin/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (res.data.success) {
        setForm((prev) => ({
          ...prev,
          logo: { ...prev.logo, [field]: res.data.url }
        }));
        toastSuccess('Logo uploaded and optimized successfully');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to upload logo');
    }
  };

  const updateLogoField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      logo: { ...prev.logo, [field]: value }
    }));
  };

  const updateMenuList = (listKey, index, field, value) => {
    setForm((prev) => {
      const items = [...prev.menu[listKey]];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, menu: { ...prev.menu, [listKey]: items } };
    });
  };

  const addMenuItem = (listKey) => {
    setForm((prev) => ({
      ...prev,
      menu: { ...prev.menu, [listKey]: [...prev.menu[listKey], emptyMenuItem()] },
    }));
  };

  const removeMenuItem = (listKey, index) => {
    setForm((prev) => ({
      ...prev,
      menu: { ...prev.menu, [listKey]: prev.menu[listKey].filter((_, i) => i !== index) },
    }));
  };

  const moveMenuItem = (listKey, index, direction) => {
    const target = index + direction;
    setForm((prev) => {
      const items = [...prev.menu[listKey]];
      if (target < 0 || target >= items.length) return prev;
      [items[index], items[target]] = [items[target], items[index]];
      return { ...prev, menu: { ...prev.menu, [listKey]: items } };
    });
  };

  const updateMainLink = (key, field, value) => {
    setForm((prev) => ({
      ...prev,
      menu: {
        ...prev.menu,
        mainLinks: {
          ...prev.menu.mainLinks,
          [key]: { ...prev.menu.mainLinks[key], [field]: value },
        },
      },
    }));
  };

  const updateFooterField = (field, value) => {
    setForm((prev) => ({ ...prev, footer: { ...prev.footer, [field]: value } }));
  };

  const updateConsultationText = (key, value) => {
    setForm((prev) => ({
      ...prev,
      footer: {
        ...prev.footer,
        freeConsultationTexts: { ...prev.footer.freeConsultationTexts, [key]: value },
      },
    }));
  };

  const updateSocialLink = (key, value) => {
    setForm((prev) => ({
      ...prev,
      footer: {
        ...prev.footer,
        socialLinks: { ...prev.footer.socialLinks, [key]: value },
      },
    }));
  };

  const updateFooterSection = (secIndex, field, value) => {
    setForm((prev) => {
      const sections = [...prev.footer.sections];
      sections[secIndex] = { ...sections[secIndex], [field]: value };
      return { ...prev, footer: { ...prev.footer, sections } };
    });
  };

  const updateFooterLink = (secIndex, linkIndex, field, value) => {
    setForm((prev) => {
      const sections = [...prev.footer.sections];
      const links = [...sections[secIndex].links];
      links[linkIndex] = { ...links[linkIndex], [field]: value };
      sections[secIndex] = { ...sections[secIndex], links };
      return { ...prev, footer: { ...prev.footer, sections } };
    });
  };

  const addFooterSection = () => {
    setForm((prev) => ({
      ...prev,
      footer: { ...prev.footer, sections: [...prev.footer.sections, emptyFooterSection()] },
    }));
  };

  const removeFooterSection = (secIndex) => {
    if (!window.confirm('Remove this footer section?')) return;
    setForm((prev) => ({
      ...prev,
      footer: { ...prev.footer, sections: prev.footer.sections.filter((_, i) => i !== secIndex) },
    }));
  };

  const addFooterLink = (secIndex) => {
    setForm((prev) => {
      const sections = [...prev.footer.sections];
      sections[secIndex] = {
        ...sections[secIndex],
        links: [...sections[secIndex].links, emptyFooterLink()],
      };
      return { ...prev, footer: { ...prev.footer, sections } };
    });
  };

  const removeFooterLink = (secIndex, linkIndex) => {
    setForm((prev) => {
      const sections = [...prev.footer.sections];
      sections[secIndex] = {
        ...sections[secIndex],
        links: sections[secIndex].links.filter((_, i) => i !== linkIndex),
      };
      return { ...prev, footer: { ...prev.footer, sections } };
    });
  };

  const renderMenuItems = (listKey, title) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase text-gray-500">{title}</h3>
        <button
          type="button"
          onClick={() => addMenuItem(listKey)}
          className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
        >
          <FaPlus className="text-xs" /> Add
        </button>
      </div>
      {form.menu[listKey].map((item, index) => (
        <div
          key={item._id || index}
          className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_auto] gap-2 items-start p-3 rounded-xl border border-gray-100 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-900/30"
        >
          <input
            className="input-modern"
            placeholder="Label"
            value={item.label}
            onChange={(e) => updateMenuList(listKey, index, 'label', e.target.value)}
          />
          <input
            className="input-modern"
            placeholder="Path (e.g. /bookkeeping)"
            value={item.path}
            onChange={(e) => updateMenuList(listKey, index, 'path', e.target.value)}
          />
          <select
            className="input-modern"
            value={item.iconName}
            onChange={(e) => updateMenuList(listKey, index, 'iconName', e.target.value)}
          >
            {MENU_ICONS.map((ic) => (
              <option key={ic} value={ic}>{ic}</option>
            ))}
          </select>
          <div className="flex gap-1">
            <button type="button" onClick={() => moveMenuItem(listKey, index, -1)} className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-700" title="Move up">
              <FaArrowUp className="text-xs" />
            </button>
            <button type="button" onClick={() => moveMenuItem(listKey, index, 1)} className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-700" title="Move down">
              <FaArrowDown className="text-xs" />
            </button>
            <button type="button" onClick={() => removeMenuItem(listKey, index)} className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Remove">
              <FaTrash className="text-xs" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <PageLayout>
      <PageHeader
        title="Menu & Footer CMS"
        description="Edit main website navigation, dropdown menus, and footer content"
        icon={FaBars}
        actions={
          <div className="flex gap-2 flex-wrap">
            <button onClick={handleReset} disabled={saving || loading} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-medium flex items-center gap-2">
              <FaUndo /> Reload Defaults
            </button>
            <button onClick={handleSave} disabled={saving || loading} className="btn-premium flex items-center gap-2">
              <FaSave /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      <div className="flex gap-2 mb-6 flex-wrap">
        {['menu', 'footer', 'logos'].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-xl text-sm font-medium capitalize transition-all ${
              activeTab === tab
                ? 'bg-primary-600 text-white shadow-md'
                : 'bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {loading ? (
        <SkeletonLoader variant="page" />
      ) : activeTab === 'logos' ? (
        <div className="space-y-6">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-4">Main Website Brand Logos</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Menu Logo Light */}
              <div className="border border-gray-150 dark:border-dark-700 p-4 rounded-xl space-y-3 bg-gray-50/10 dark:bg-dark-900/5 flex flex-col justify-between">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Menu Header Logo (Light Mode / Dark BG)</label>
                  <div className="flex gap-2 items-center mt-1.5">
                    <input
                      className="input-modern text-xs py-1 flex-1"
                      value={form.logo.menuLogoLight || ''}
                      onChange={(e) => updateLogoField('menuLogoLight', e.target.value)}
                      placeholder="Image URL..."
                    />
                    <label className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-300 dark:border-dark-600 rounded-lg text-xs font-medium hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer whitespace-nowrap">
                      <FaUpload className="w-3 h-3" /> Upload
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoUpload(e, 'menuLogoLight')}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
                {form.logo.menuLogoLight && (
                  <div className="relative w-full h-20 rounded-lg overflow-hidden border border-gray-200 bg-[#020c1b] flex items-center justify-center p-3 mt-3">
                    <img
                      src={form.logo.menuLogoLight.startsWith('http') ? form.logo.menuLogoLight : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${form.logo.menuLogoLight}`}
                      alt="Menu Light Logo Preview"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                )}
              </div>

              {/* Menu Logo Dark */}
              <div className="border border-gray-150 dark:border-dark-700 p-4 rounded-xl space-y-3 bg-gray-50/10 dark:bg-dark-900/5 flex flex-col justify-between">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Menu Header Logo (Dark Mode / Light BG)</label>
                  <div className="flex gap-2 items-center mt-1.5">
                    <input
                      className="input-modern text-xs py-1 flex-1"
                      value={form.logo.menuLogoDark || ''}
                      onChange={(e) => updateLogoField('menuLogoDark', e.target.value)}
                      placeholder="Image URL..."
                    />
                    <label className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-300 dark:border-dark-600 rounded-lg text-xs font-medium hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer whitespace-nowrap">
                      <FaUpload className="w-3 h-3" /> Upload
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoUpload(e, 'menuLogoDark')}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
                {form.logo.menuLogoDark && (
                  <div className="relative w-full h-20 rounded-lg overflow-hidden border border-gray-200 bg-white flex items-center justify-center p-3 mt-3">
                    <img
                      src={form.logo.menuLogoDark.startsWith('http') ? form.logo.menuLogoDark : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${form.logo.menuLogoDark}`}
                      alt="Menu Dark Logo Preview"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                )}
              </div>

              {/* Footer Logo */}
              <div className="border border-gray-150 dark:border-dark-700 p-4 rounded-xl space-y-3 bg-gray-50/10 dark:bg-dark-900/5 flex flex-col justify-between">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Footer Logo</label>
                  <div className="flex gap-2 items-center mt-1.5">
                    <input
                      className="input-modern text-xs py-1 flex-1"
                      value={form.logo.footerLogo || ''}
                      onChange={(e) => updateLogoField('footerLogo', e.target.value)}
                      placeholder="Image URL..."
                    />
                    <label className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-300 dark:border-dark-600 rounded-lg text-xs font-medium hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer whitespace-nowrap">
                      <FaUpload className="w-3 h-3" /> Upload
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoUpload(e, 'footerLogo')}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
                {form.logo.footerLogo && (
                  <div className="relative w-full h-20 rounded-lg overflow-hidden border border-gray-200 bg-[#020c1b] flex items-center justify-center p-3 mt-3">
                    <img
                      src={form.logo.footerLogo.startsWith('http') ? form.logo.footerLogo : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${form.logo.footerLogo}`}
                      alt="Footer Logo Preview"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                )}
              </div>

              {/* Logo Alt Text */}
              <div className="border border-gray-150 dark:border-dark-700 p-4 rounded-xl space-y-3 bg-gray-50/10 dark:bg-dark-900/5 flex flex-col justify-between">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Logo Alt Title Text</label>
                  <input
                    className="input-modern text-sm mt-1.5"
                    value={form.logo.logoAlt || ''}
                    onChange={(e) => updateLogoField('logoAlt', e.target.value)}
                    placeholder="BooksComply"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'menu' ? (
        <div className="space-y-6">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            {renderMenuItems('servicesItems', 'Services Dropdown')}
            {renderMenuItems('industryItems', 'Industry Dropdown')}
          </div>

          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
            <h3 className="text-sm font-bold uppercase text-gray-500">Main Nav Links & Buttons</h3>
            {MAIN_LINK_KEYS.map(({ key, label, hasPath }) => (
              <div key={key} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-center p-3 rounded-xl border border-gray-100 dark:border-dark-700">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
                <div className="flex gap-2">
                  <input
                    className="input-modern flex-1"
                    placeholder="Label"
                    value={form.menu.mainLinks[key]?.label || ''}
                    onChange={(e) => updateMainLink(key, 'label', e.target.value)}
                  />
                  {hasPath && (
                    <input
                      className="input-modern flex-1"
                      placeholder="Path"
                      value={form.menu.mainLinks[key]?.path || ''}
                      onChange={(e) => updateMainLink(key, 'path', e.target.value)}
                    />
                  )}
                </div>
                <label className="flex items-center gap-2 text-sm whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={form.menu.mainLinks[key]?.show !== false}
                    onChange={(e) => updateMainLink(key, 'show', e.target.checked)}
                  />
                  Show
                </label>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
            <h3 className="text-sm font-bold uppercase text-gray-500">General</h3>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase text-gray-500">Tagline</label>
              <textarea className="input-modern min-h-[70px]" value={form.footer.tagline} onChange={(e) => updateFooterField('tagline', e.target.value)} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase text-gray-500">Contact Email</label>
                <input className="input-modern" value={form.footer.contactEmail} onChange={(e) => updateFooterField('contactEmail', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase text-gray-500">Copyright Text</label>
                <input className="input-modern" value={form.footer.copyrightText} onChange={(e) => updateFooterField('copyrightText', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
            <h3 className="text-sm font-bold uppercase text-gray-500">Social Links</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['linkedin', 'twitter', 'instagram'].map((key) => (
                <div key={key} className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase text-gray-500 capitalize">{key}</label>
                  <input className="input-modern" value={form.footer.socialLinks[key] || ''} onChange={(e) => updateSocialLink(key, e.target.value)} />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
            <h3 className="text-sm font-bold uppercase text-gray-500">Free Consultation Texts</h3>
            {CONSULTATION_KEYS.map(({ key, label }) => (
              <div key={key} className="space-y-1.5">
                <label className="text-xs font-semibold uppercase text-gray-500">{label}</label>
                <textarea className="input-modern min-h-[60px]" value={form.footer.freeConsultationTexts[key] || ''} onChange={(e) => updateConsultationText(key, e.target.value)} />
              </div>
            ))}
          </div>

          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase text-gray-500">Footer Sections</h3>
              <button type="button" onClick={addFooterSection} className="text-sm text-primary-600 flex items-center gap-1">
                <FaPlus className="text-xs" /> Add Section
              </button>
            </div>
            {form.footer.sections.map((section, secIndex) => (
              <div key={section._id || secIndex} className="p-4 rounded-xl border border-gray-100 dark:border-dark-700 space-y-3">
                <div className="flex gap-2 items-center">
                  <input
                    className="input-modern flex-1"
                    placeholder="Section title (e.g. SERVICES)"
                    value={section.title}
                    onChange={(e) => updateFooterSection(secIndex, 'title', e.target.value)}
                  />
                  <button type="button" onClick={() => removeFooterSection(secIndex)} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                    <FaTrash />
                  </button>
                </div>
                {section.links.map((link, linkIndex) => (
                  <div key={link._id || linkIndex} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
                    <input className="input-modern" placeholder="Link label" value={link.label} onChange={(e) => updateFooterLink(secIndex, linkIndex, 'label', e.target.value)} />
                    <input className="input-modern" placeholder="Path" value={link.path} onChange={(e) => updateFooterLink(secIndex, linkIndex, 'path', e.target.value)} />
                    <button type="button" onClick={() => removeFooterLink(secIndex, linkIndex)} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                      <FaTrash className="text-xs" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => addFooterLink(secIndex)} className="text-sm text-primary-600 flex items-center gap-1">
                  <FaPlus className="text-xs" /> Add Link
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default NavigationCMS;
